/**
 * /partner/auszahlungen – Saldo, Anforderungsbutton, Provisions- und Auszahlungs-Historie,
 * Stammdaten-Pflege (IBAN, Adresse, USt-Status).
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import PartnerLayout from "@/components/partner/PartnerLayout";
import PartnerPayoutForm from "@/components/partner/PartnerPayoutForm";
import PartnerRequestPayoutCard from "@/components/partner/PartnerRequestPayoutCard";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMyAffiliate } from "@/hooks/useMyAffiliate";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Download } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const formatEur = (cents: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format((cents || 0) / 100);

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("de-DE") : "-";

const commissionStatusVariant: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending: { label: "Wird gesammelt", variant: "secondary" },
  approved: { label: "Auszahlbar", variant: "default" },
  paid: { label: "Ausgezahlt", variant: "outline" },
  refunded: { label: "Storniert", variant: "destructive" },
  canceled: { label: "Storniert", variant: "destructive" },
};

const payoutStatusVariant: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  requested: { label: "Angefordert", variant: "secondary" },
  pending: { label: "In Bearbeitung", variant: "secondary" },
  paid: { label: "Ausgezahlt", variant: "default" },
  canceled: { label: "Storniert", variant: "destructive" },
};

const PartnerAuszahlungen = () => {
  const { data: user } = useCurrentUser();
  const { data: affiliate, isLoading: loadingAffiliate } = useMyAffiliate(user?.id);

  const { data: commissions } = useQuery({
    queryKey: ["partner-commissions", affiliate?.id],
    enabled: !!affiliate?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliate_commissions")
        .select("id, status, commission_amount, gross_amount, product_type, earned_at, paid_at")
        .eq("affiliate_id", affiliate!.id)
        .order("earned_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: payouts } = useQuery({
    queryKey: ["partner-payouts", affiliate?.id],
    enabled: !!affiliate?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliate_payouts")
        .select("id, total_amount, status, transferred_at, requested_at, period_from, period_to, reference, credit_note_id")
        .eq("affiliate_id", affiliate!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const handleDownloadCreditNote = async (creditNoteId: string) => {
    try {
      const { data: cn, error } = await supabase
        .from("affiliate_credit_notes")
        .select("storage_path, credit_note_number")
        .eq("id", creditNoteId)
        .maybeSingle();
      if (error) throw error;
      if (!cn?.storage_path) {
        toast({ title: "Gutschrift wird gerade erstellt", variant: "default" });
        return;
      }
      const { data: signed, error: sErr } = await supabase.storage
        .from("affiliate-documents")
        .createSignedUrl(cn.storage_path, 3600);
      if (sErr) throw sErr;
      window.open(signed.signedUrl, "_blank");
    } catch (err) {
      toast({ title: "Download fehlgeschlagen", description: (err as Error).message, variant: "destructive" });
    }
  };

  if (loadingAffiliate) {
    return (
      <PartnerLayout title="Auszahlungen">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Lade Daten…
        </div>
      </PartnerLayout>
    );
  }

  if (!affiliate) {
    return (
      <PartnerLayout title="Auszahlungen">
        <p className="text-sm text-muted-foreground">Kein Partner-Konto gefunden.</p>
      </PartnerLayout>
    );
  }

  return (
    <PartnerLayout title="Auszahlungen">
      <div className="grid gap-6">
        <PartnerRequestPayoutCard affiliate={affiliate} />
        <PartnerPayoutForm affiliate={affiliate} userId={user!.id} />

        <Card>
          <CardHeader>
            <CardTitle>Provisionen</CardTitle>
            <CardDescription>Die letzten 100 Buchungen.</CardDescription>
          </CardHeader>
          <CardContent>
            {!commissions ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            ) : commissions.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine Provisionen.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Datum</TableHead>
                      <TableHead>Produkt</TableHead>
                      <TableHead className="text-right">Brutto</TableHead>
                      <TableHead className="text-right">Provision</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissions.map((c) => {
                      const meta = commissionStatusVariant[c.status] ?? { label: c.status, variant: "outline" as const };
                      return (
                        <TableRow key={c.id}>
                          <TableCell>{formatDate(c.earned_at)}</TableCell>
                          <TableCell className="text-xs">{c.product_type ?? "-"}</TableCell>
                          <TableCell className="text-right">{formatEur(Number(c.gross_amount ?? 0))}</TableCell>
                          <TableCell className="text-right font-medium">{formatEur(Number(c.commission_amount ?? 0))}</TableCell>
                          <TableCell><Badge variant={meta.variant}>{meta.label}</Badge></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Auszahlungshistorie</CardTitle>
          </CardHeader>
          <CardContent>
            {!payouts ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            ) : payouts.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine Auszahlungen.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Zeitraum</TableHead>
                    <TableHead className="text-right">Betrag</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Gutschrift</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.map((p) => {
                    const meta = payoutStatusVariant[p.status] ?? { label: p.status, variant: "outline" as const };
                    const dateShown = p.transferred_at ?? p.requested_at;
                    return (
                      <TableRow key={p.id}>
                        <TableCell>{formatDate(dateShown)}</TableCell>
                        <TableCell className="text-xs">
                          {formatDate(p.period_from)} – {formatDate(p.period_to)}
                        </TableCell>
                        <TableCell className="text-right font-medium">{formatEur(Number(p.total_amount ?? 0))}</TableCell>
                        <TableCell><Badge variant={meta.variant}>{meta.label}</Badge></TableCell>
                        <TableCell>
                          {p.credit_note_id ? (
                            <Button size="sm" variant="outline" onClick={() => handleDownloadCreditNote(p.credit_note_id!)}>
                              <Download className="mr-1 h-3 w-3" /> PDF
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">–</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </PartnerLayout>
  );
};

export default PartnerAuszahlungen;
